import React, { Dispatch, SetStateAction, useState } from 'react';
import { FaExclamationCircle } from 'react-icons/fa';
import { TagsInput } from 'react-tag-input-component';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { SettingsDA } from '../../services/admin/settings.service';
import { Error } from '../../models/error.model';
import { Company } from '../../models/company.model';
import { GlobalSwal } from '../../styles/swal.style';
import { Feature } from '../../models/feature.model';
import { FeatureType, FeatureInputType } from '../../models/settings.features.model';
import { FeatureDetail } from '../../models/feature.detail.model';
import { legendIconValidInvalidRestart, legendIconInvalid, legendValidInvalidRestart, legendInvalid, legendValid } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { evaluateLegendRequiredIcon } from '../../scripts/validate.legend.script';
import { expressions } from '../../libraries/regular.expressions.library';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { inventoryTypeValue } from '../../libraries/inventory.type.library';

import AppLegend from '../element/Legend';

export interface AppModalSettingsCreateFeatureProps {
  companyForUser: Company | null,
  type: string,
  features?: {value: FeatureDetail[], valid: boolean[]},
  variantDefault?: {value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}, valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}},
  variants?: {value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}[], valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}[]},
  setFeatures?: Dispatch<SetStateAction<{value: FeatureDetail[], valid: boolean[]}>>,
  setVariantDefault?: Dispatch<SetStateAction<{value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}, valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}}>>,
  setVariants?: Dispatch<SetStateAction<{value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}[], valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}[]}>>,
  loadSettingsForCompany: Function
};

let errorResponse: Error;

const AppModalSettingsCreateFeature: React.FunctionComponent<AppModalSettingsCreateFeatureProps> = ({companyForUser, type, features, variantDefault, variants, setFeatures, setVariantDefault, setVariants, loadSettingsForCompany}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [inputType, setInputType] = useState({value: 'select', valid: true})
  const [name, setName] = useState({value: '', valid: false})
  const [options, setOptions] = useState({value: [] as string[], valid: false})

  const submitSettingsParameterCreate = async () => {
    setLoadIndicator('on')

    const optionsValid = inputType.value === 'free' ? true : options.valid

    if (companyForUser && name.valid && optionsValid) {
      await SettingsDA.postSettingFeature(companyForUser.id, type as FeatureType, name.value, inputType.value as FeatureInputType, inputType.value === 'select' ? options.value : []).then( (response) => {
        if (response.status === 201 || response.status === 200) {
          let tempFeature = response.data as Feature

          loadSettingsForCompany(companyForUser.id)

          if (features && setFeatures) {
            setFeatures({value: [...features.value, {id_feature: tempFeature.id, name: tempFeature.name, value: ''}], valid: [...features.valid, false]})
          }

          if (variantDefault && variants && setVariantDefault && setVariants) {
            if (variants.value.length > 1 && variants.valid.length > 1) {
              setVariants({value: variants.value.map((variant) => ({...variant, features: [...variant.features, {id_feature: tempFeature.id, name: tempFeature.name, value: ''}]})), valid: variants.valid.map((variant) => ({...variant, features: [...variant.features, false]}))})
            } else {
              setVariantDefault({value: {...variantDefault.value, features: [...variantDefault.value.features , {id_feature: tempFeature.id, name: tempFeature.name, value: ''}]}, valid: {...variantDefault.valid, features: [...variantDefault.valid.features, false]}})
            }
          }

          GlobalSwal.fire({
            title: lang.labels.successfullyCreatedCompanyParameters,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalSettingFeatureCreate()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!name.value.length) legendIconInvalid('modal-settings-create-feature-input-name', 'modal-settings-create-feature-legend-name-required')
        if (inputType.value === 'select' && options.value.length < 1) legendInvalid('modal-settings-create-legend-options-required')
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeInputType = (event: React.ChangeEvent <HTMLSelectElement>) => {
    setInputType({value: event.target.value, valid: true})
    if (event.target.value === 'free') {
      setOptions({value: [], valid: false})
    }
  }

  const handleChangeName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setName({...name, value: event.target.value})
  }

  const handleChangeOptions = (items: string[]) => {
    setOptions({...options, value: items})
  }

  const validateName = () => {
    evaluateLegendRequiredIcon(expressions.text, name, setName, 'modal-settings-create-feature-input-name', 'modal-settings-create-feature-legend-name-valid', 'modal-settings-create-feature-legend-name-required')
  }

  const validateOptionsKeyUp = (event: React.KeyboardEvent <HTMLFormElement | HTMLInputElement>) => {
    if (expressions && expressions.tag.test(event.currentTarget.value)) {
      if (!options.value.includes(event.currentTarget.value)) {
        setOptions({...options, valid: true})
        legendValid('modal-settings-create-feature-legend-options-valid')
        legendValid('modal-settings-create-feature-legend-options-exists')
      } else {
        setOptions({...options, valid: false})
        legendInvalid('modal-settings-create-feature-legend-options-exists')
      }
    } else {
      setOptions({...options, valid: false})
      legendInvalid('modal-settings-create-feature-legend-options-valid')
    }
  }

  const validateOptionsBlur = (event: React.FocusEvent <HTMLFormElement | HTMLInputElement>) => {
    if (options.value.length > 0) {
      setOptions({...options, valid: true})
      legendValid('modal-settings-create-feature-legend-options-required')
    } else {
      setOptions({...options, valid: false})
      legendInvalid('modal-settings-create-feature-legend-options-required')
    }

    if (options.value.includes(event.currentTarget.value)) {
      setOptions({...options, valid: true})
      legendValidInvalidRestart('modal-settings-create-feature-legend-options-exists')
    }

    event.currentTarget.value = ''
    legendValidInvalidRestart('modal-settings-create-feature-legend-options-valid')
  }

  const validateOptionsAdd = (item: string, items: string[]) => {
    if (expressions && items) {
      if (expressions.tag.test(item)) {
        setOptions({...options, valid: true})
        legendValid('modal-settings-create-feature-legend-options-valid')
        legendValid('modal-settings-create-feature-legend-options-required')
        return true;
      } else {
        setOptions({...options, valid: false})
        legendInvalid('modal-settings-create-feature-legend-options-valid')
        return false;
      }
    } else {
      setOptions({...options, valid: false})
      legendValidInvalidRestart('modal-settings-create-feature-legend-options-valid')
      legendValidInvalidRestart('modal-settings-create-feature-legend-options-required')
      return false;
    }
  }

  const validateOptionsExisting = (item: string) => {
    if (item.length > 0) {
      setOptions({...options, valid: false})
      legendInvalid('modal-settings-create-feature-legend-options-exists')
    } else {
      setOptions({...options, valid: true})
      legendValid('modal-settings-create-feature-legend-options-exists')
    }
  }

  const validateOptionsRemoved = (item: string) => {
    if (item.length > 0 && options.value.length === 1) {
      setOptions({...options, valid: false})
      legendInvalid('modal-settings-create-feature-legend-options-required')
    } else {
      setOptions({...options, valid: true})
      legendValid('modal-settings-create-feature-legend-options-required')
    }
  }

  const executeHideModalSettingFeatureCreate = () => {
    modalHide('modal-settings-create-feature')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendIconValidInvalidRestart('modal-settings-create-feature-input-name', 'modal-settings-create-feature-legend-name-valid')
    legendIconValidInvalidRestart('modal-settings-create-feature-input-name', 'modal-settings-create-feature-legend-name-required')
    legendValidInvalidRestart('modal-settings-create-legend-options-valid')
    legendValidInvalidRestart('modal-settings-create-legend-options-required')
    legendValidInvalidRestart('modal-settings-create-legend-options-exists')

    setInputType({value: 'select', valid: true})
    setName({value: '', valid: false})
    setOptions({value: [], valid: false})
  }

  return (
    <div id="modal-settings-create-feature" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.addFeatureParameter}</h2>
          </div>
          <div className="modal-body">
            <div className="form">
              <div className="row mb-2">
                <label className="col-lg-3 col-form-label required">{lang.labels.type}</label>
                <div className="col-lg-9">
                  <input className="form-control is-valid" type="text" autoComplete="off" name="type" value={inventoryTypeValue(lang, type)} disabled />
                </div>
              </div>
              <div className="row mb-2">
                <label className="col-lg-3 col-form-label required">{lang.labels.name}</label>
                <div className="col-lg-9">
                  <input id="modal-settings-create-feature-input-name" className="form-control" type="text" autoComplete="off" name="name" value={name.value} onChange={handleChangeName} onKeyUp={validateName} onBlur={validateName} />
                  <AppLegend component={"modal-settings-create-feature"} attribute={{check: name.valid, name: "name", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                </div>
              </div>
              <div className="row mb-2">
                <label className="col-lg-3 col-form-label required">{lang.labels.inputMethod}</label>
                <div className="col-lg-9">
                  <select className="form-select" value={inputType.value} onChange={handleChangeInputType}>
                    <option value="select">{lang.labels.predefinedValues}</option>
                    <option value="free">{lang.labels.freeInput}</option>
                  </select>
                </div>
              </div>
              {inputType.value === 'select' && <div className="row">
                <label className="col-lg-3 col-form-label d-flex align-items-center">
                  <span className="required">{lang.labels.options}</span>
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.enterDesiredValuePressEnterAdd}><FaExclamationCircle /></i>
                </label>
                <div className="col-lg-9">
                  <div className="options-cell">
                      <TagsInput classNames={{ input: "form-control", tag: "fs-8 ps-2"}} value={options.value} onChange={handleChangeOptions} onKeyUp={validateOptionsKeyUp} onBlur={validateOptionsBlur} beforeAddValidate={validateOptionsAdd} onExisting={validateOptionsExisting} onRemoved={validateOptionsRemoved} />
                    </div>
                    <AppLegend component={"modal-settings-create-feature"} attribute={{check: options.valid, name: "options", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: true}}></AppLegend>
                </div>
              </div>}
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalSettingFeatureCreate}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitSettingsParameterCreate}>
              <span className="indicator-label">{lang.labels.create}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalSettingsCreateFeature;
